import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { uploadVoice, getVoices, toggleVoice } from "../../services/AppService";
import closeButton from "../../assets/images/close.png";
import { BiTrash } from "react-icons/bi";
import { resolveMediaUrl } from "../../utility/media";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import UploadLoader from "../../components/UploadLoader";

const VoiceForm = () => {

    const navigate = useNavigate();
    const { workOrderId } = useParams();

    const [file, setFile] = useState(null);
    const [voices, setVoices] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        action: null,
        payload: null
    });

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: ""
    });

    const fetchVoices = async () => {
        const res = await getVoices(workOrderId);
        setVoices(res || []);
    };
    const fileInputRef = useRef(null);

    const audioRefs = useRef([]);
    const [activeIndex, setActiveIndex] = useState(null);

    useEffect(() => {
        if (workOrderId) fetchVoices();
    }, [workOrderId]);

    const handleUpload = () => {
        if (!file) {
            setMessageModal({
                open: true,
                type: "error",
                title: "Error",
                message: "Please select an audio file"
            });
            return;
        }

        setConfirmModal({
            open: true,
            action: "upload",
            payload: null
        });
    };

    const handleConfirm = async () => {
        const { action, payload } = confirmModal;
        setConfirmModal({ open: false, action: null, payload: null });

        try {
            if (action === "upload") {
                setIsUploading(true);
                setUploadProgress(0);
                await uploadVoice(workOrderId, file, (progress) => {
                    setUploadProgress(progress);
                });
                setFile(null);

                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }

                setMessageModal({
                    open: true,
                    type: "success",
                    title: "Success",
                    message: "Voice uploaded successfully"
                });
            }

            if (action === "delete") {
                await toggleVoice(payload);

                setMessageModal({
                    open: true,
                    type: "success",
                    title: "Success",
                    message: "Voice deleted successfully"
                });
            }

            fetchVoices();

        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong";

            setMessageModal({
                open: true,
                type: "error",
                title: "Error",
                message
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
        }
    };

    const handleDelete = (id) => {
        setConfirmModal({
            open: true,
            action: "delete",
            payload: id
        });
    };

    const handlePlay = (currentIndex) => {
        audioRefs.current.forEach((audio, index) => {
            if (audio && index !== currentIndex) {
                audio.pause();
                audio.currentTime = 0; // optional (reset)
            }
        });

        setActiveIndex(currentIndex);
    };

    return (
        <div className="bg-white p-4 rounded shadow-md">

            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-lg md:text-xl font-semibold text-blue uppercase">
                    Add / Edit Voice
                </h4>
                <img src={closeButton} alt="Close"
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => navigate(-1)}
                />
            </div>

            {/* Upload */}
            <div className="flex flex-col sm:flex-row gap-3 items-center mb-4">
                <input
                    type="file"
                    accept="audio/*,audio/mp3,audio/wav,audio/m4a"
                    ref={fileInputRef}
                    onChange={(e) => setFile(e.target.files[0])}
                    className="border p-2 rounded w-full sm:w-auto bg-white"
                />
                <button
                    onClick={handleUpload}
                    disabled={!file}
                    className="staff-btn-save disabled:opacity-50"
                >
                    Add
                </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full border">
                    <thead className="bg-[#5f6db3] text-white">
                        <tr>
                            <th className="p-2 border">Sr</th>
                            <th className="p-2 border">Voice</th>
                            <th className="p-2 border">Delete</th>
                        </tr>
                    </thead>

                    <tbody>
                        {voices.map((voice, idx) => (
                            <tr key={voice.id} className="text-center">

                                <td className="border p-2">{idx + 1}</td>

                                <td className="border p-2">
                                    <audio controls className="mx-auto"
                                        ref={(el) => (audioRefs.current[idx] = el)}
                                        onPlay={() => handlePlay(idx)}
                                    >
                                        <source
                                            src={resolveMediaUrl(voice.fileLocation)}
                                            type="audio/mpeg"
                                        />
                                        Your browser does not support audio
                                    </audio>
                                </td>

                                <td className="border p-2">
                                    <button
                                        onClick={() => handleDelete(voice.id)}
                                        className="text-red-600 text-xl"
                                    >
                                        <BiTrash />
                                    </button>
                                </td>

                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modals */}
            <ConfirmActionModal
                open={confirmModal.open}
                onClose={() => setConfirmModal({ open: false })}
                onConfirm={handleConfirm}
                title={
                    confirmModal.action === "upload"
                        ? "Confirm Upload"
                        : "Confirm Delete"
                }
                message={
                    confirmModal.action === "upload"
                        ? "Are you sure you want to upload this voice?"
                        : "Are you sure you want to delete this voice?"
                }
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />

            {isUploading && (
                <UploadLoader
                    text="Uploading voice recording, please wait..."
                    progress={uploadProgress}
                />
            )}
        </div>
    );
};

export default VoiceForm;